import React, { useEffect, useState } from 'react';

import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import MultiSelect from 'Components-redesign/Fields/MultiSelect';
import Select from 'Components-redesign/Fields/Select';
import { useI18n, useSelector } from 'Hooks';

import { GetVenues } from './__queries__';

const VENUES_QUERY = gql`
  query GetVenues($companyId: ID!) {
    venues: allVenues(filter: { company: { id: $companyId } }) {
      id
      name
      deleted
    }
  }
`;

type Props =
  | {
      onIDChange: (id: string[]) => void;
      multiple: true;
      value?: string[];
      startValueEmpty?: boolean;
      showDeleted?: boolean;
    }
  | {
      onIDChange: (id: string) => void;
      multiple?: false;
      value?: string[];
      startValueEmpty?: boolean;
      showDeleted?: boolean;
    };

/**
 * Easy drop-in component for picking a venue.
 */
const VenuePicker: React.FC<Props> = ({
  onIDChange,
  multiple,
  value,
  startValueEmpty,
  showDeleted,
}) => {
  const { t } = useI18n();

  const companyId = useSelector((state) => state.company);
  const userPermissions = useSelector((state) => state.userPermissions);

  const [venueIds, setVenueIds] = useState<string[]>([]);

  const venueQuery = useQuery<GetVenues>(VENUES_QUERY, {
    variables: {
      companyId,
    },
    skip: !companyId,
  });

  /* Filter out venues that user don't have permission to */
  const venues = venueQuery.data?.venues || [];
  const filteredVenues = venues
    .filter(
      (venue) =>
        userPermissions?.venueIds.length === 0 || userPermissions?.venueIds.includes(venue.id),
    )
    .filter((venue) => showDeleted || !venue.deleted)
    .map((venue) => ({
      ...venue,
      name: venue.deleted && showDeleted ? `${venue.name} (${t('General.Deleted')})` : venue.name,
    }));

  useEffect(() => {
    if (!venueIds.length && filteredVenues.length === 1) {
      setVenueIds([filteredVenues[0].id]);
      return;
    }

    if (startValueEmpty) {
      return;
    }

    if (!venueIds.length && filteredVenues.length) {
      if (value) {
        // Filter `value` to ensure it only includes IDs present in `venues`
        const filteredValue = value.filter((id) => venues.some((venue) => venue.id === id));
        setVenueIds(filteredValue.length ? filteredValue : [filteredVenues[0].id]);
      } else {
        setVenueIds([filteredVenues[0].id]);
      }
    }
  }, [filteredVenues]);

  useEffect(() => {
    if (multiple) {
      onIDChange(venueIds);
    } else {
      onIDChange(venueIds[0]);
    }
  }, [venueIds]);

  /** If this is a single-venue company, just show a disabled venue selector with the single venue */
  if (filteredVenues.length === 1) {
    return (
      <Select
        name="venue"
        placeholder={filteredVenues[0].name}
        disabled
        selected={filteredVenues[0]}
        list={filteredVenues}
        handleChange={(name, venue) => setVenueIds([venue.id])}
      />
    );
  }

  if (multiple) {
    return (
      <MultiSelect
        name="venue"
        placeholder={'All venues'}
        selected={value ? value : venueIds}
        handleChange={(ids) => setVenueIds(ids)}
        list={filteredVenues}
      />
    );
  }

  const selectedId = value ? value[0] : venueIds[0];

  return (
    <Select
      name="venue"
      placeholder={venueQuery.loading ? 'Loading...' : 'Choose venue'}
      selected={filteredVenues.find((v) => v.id === selectedId)}
      list={filteredVenues}
      handleChange={(name, venue) => setVenueIds([venue.id])}
    />
  );
};

export default VenuePicker;
